/**
 * ONE place that classifies `stone.media.videoUrl`.
 *
 * This used to be copy-pasted into MediaModal.tsx, StoneDetailScreen.tsx and
 * (in a third variant) StoneImage.tsx, all sniffing the file extension off the
 * bare path. That triplication is why the server could not change the shape of
 * videoUrl without silently killing the 360 tab on the whole catalog — the
 * masked form has no extension and isn't absolute, so every copy classified it
 * as "no video at all".
 *
 * Three kinds:
 *   'mp4'    — a real clip. `/api/v/video/<sealed>.mp4` (masked) or a bucket
 *              URL. Plays in a <video>.
 *   'spin'   — `/api/v/360/<sealed>`: an OPAQUE handle to a supplier 360-viewer
 *              page, served to callers who may not see the refStockId (the
 *              viewer URL spells it out — `?d=<REF>`). Only our own Diaex360
 *              player can render it, via /v/frames. There is deliberately NO
 *              iframe fallback: the token is not a page, and the whole point is
 *              that the upstream URL never reaches this browser.
 *   'iframe' — the real supplier viewer URL, served only to a KYC-approved
 *              caller. Diaex360 first, legacy sandboxed <iframe> if the frame
 *              sequence can't be resolved.
 */
export type VideoKind = 'mp4' | 'spin' | 'iframe' | null

/** Must match functions/src/v/index.ts::MASKED_360_PREFIX. */
export const MASKED_360_PREFIX = '/api/v/360/'

export function videoKindOf(url: string | null | undefined): VideoKind {
  if (!url) return null
  if (url.startsWith(MASKED_360_PREFIX)) return 'spin'
  const bare = url.split(/[?#]/)[0].toLowerCase()
  if (/\.(mp4|webm|mov|m4v)$/.test(bare)) return 'mp4'
  if (/^https?:\/\//i.test(url)) return 'iframe'
  return null
}

/** A 360 viewer (masked or raw) — the only thing /v/frames can resolve. A clip
 *  has no frame sequence, so callers skip the round-trip. */
export function resolvesToFrames(url: string | null | undefined): boolean {
  const k = videoKindOf(url)
  return k === 'spin' || k === 'iframe'
}

/**
 * Ask OUR photo proxy for a thumbnail-sized rendition: appends `&w=<width>` to
 * `/api/v/photo?…` and `/api/v/img?…` URLs ONLY (both always carry `?t=`
 * already). A 40px table thumb was downloading the full-size supplier still —
 * measured 20.6MB of image bytes on one /search cards page.
 *
 * Anything else passes through untouched: external/absolute supplier URLs
 * (a KYC-revealed caller's raw media must never grow our query params),
 * bundled assets (/diamonds/*.webp), and URLs that already carry a `w`. The
 * server clamps the value onto its fixed ladder (96/256/512/1024) and falls
 * back to full-size bytes on any resize failure, so this is only ever a hint.
 */
export function withProxyWidth(url: string, width?: number): string {
  if (!width || !Number.isFinite(width) || width <= 0) return url
  if (!/^\/api\/v\/(photo|img)\?/.test(url)) return url
  if (/[?&]w=/.test(url)) return url
  return `${url}&w=${Math.round(width)}`
}
